import { create } from 'zustand';
import type { MapFloorLevel, MapObjectInstance } from '../content/schema/map.js';
import { getObjectDefinition } from '../content/registry/objectRegistry.js';
import { makeObjectId } from '../editor/store/mapEditorStore.js';

/**
 * The live, mutable object-instance state for a running (`mode: 'play'`)
 * scene. Play mode previously rendered `MapObjectInstance[]` straight off the
 * static, immutable `mapDocument` prop (see `LevelRenderer`'s own doc
 * comment) - there was no live runtime entity concept to spawn, move or
 * remove against. `GameRuntime` seeds this store from `mapDocument` once per
 * play session; `LevelRenderer` then reads from here instead of the document
 * directly while playing, so a script (or any future runtime system) can
 * mutate the running scene without ever touching the authored document.
 *
 * Deliberately not persisted anywhere: a spawned/moved/removed instance lives
 * only for the current play session. Reloading a project re-seeds from the
 * unchanged, authored `mapDocument`.
 */
export interface LiveSceneStore {
  readonly objectInstances: readonly MapObjectInstance[];
  /** Bumped on every `seed`, so a consumer can tell a fresh session apart from a mutation of the current one. */
  readonly generation: number;
  seed: (instances: readonly MapObjectInstance[]) => void;
  spawn: (assetId: string, floor: MapFloorLevel, position: [number, number, number], rotationY?: number) => string;
  getInstance: (id: string) => MapObjectInstance | undefined;
  setPosition: (id: string, position: [number, number, number]) => boolean;
  remove: (id: string) => boolean;
}

export const useLiveSceneStore = create<LiveSceneStore>((set, get) => ({
  objectInstances: [],
  generation: 0,

  seed: (instances) => set((state) => ({ objectInstances: [...instances], generation: state.generation + 1 })),

  spawn: (assetId, floor, position, rotationY) => {
    if (!getObjectDefinition(assetId)) {
      throw new Error(`Unknown assetId "${assetId}" - it is not registered in the object registry.`);
    }
    const id = makeObjectId(assetId);
    const instance: MapObjectInstance = {
      id,
      assetId,
      floor,
      transform: rotationY === undefined ? { position } : { position, rotation: [0, rotationY, 0] },
    };
    set((state) => ({ objectInstances: [...state.objectInstances, instance] }));
    return id;
  },

  getInstance: (id) => get().objectInstances.find((instance) => instance.id === id),

  setPosition: (id, position) => {
    let found = false;
    set((state) => ({
      objectInstances: state.objectInstances.map((instance) => {
        if (instance.id !== id) return instance;
        found = true;
        return { ...instance, transform: { ...instance.transform, position } };
      }),
    }));
    return found;
  },

  remove: (id) => {
    let found = false;
    set((state) => ({
      objectInstances: state.objectInstances.filter((instance) => {
        if (instance.id !== id) return true;
        found = true;
        return false;
      }),
    }));
    return found;
  },
}));

/**
 * Reset to an empty, unseeded state. Called on `GameRuntime` unmount so a
 * fresh play session never inherits a previous one's spawned/moved/removed
 * entities (the store is a module-level singleton, not scoped per mount).
 */
export function resetLiveSceneStore(): void {
  useLiveSceneStore.setState({ objectInstances: [], generation: 0 });
}
